import { useEffect } from 'react';

interface DocumentMeta {
  title: string;
  description?: string;
  canonicalPath?: string;
  image?: string;
  /** JSON-LD structured data injected as a <script type="application/ld+json">. */
  jsonLd?: Record<string, unknown>;
  type?: 'website' | 'product';
}

const SITE_NAME = 'Kon Store';

function upsertMeta(attr: 'name' | 'property', key: string, content: string): void {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function upsertCanonical(href: string): void {
  let el = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', 'canonical');
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

/**
 * Declaratively manage per-page document metadata: title, description, Open
 * Graph tags, canonical URL, and product JSON-LD. Cleans up the JSON-LD node
 * on unmount so structured data never leaks between routes.
 */
export function useDocumentMeta(meta: DocumentMeta): void {
  const { title, description, canonicalPath, image, jsonLd, type = 'website' } = meta;

  useEffect(() => {
    const fullTitle = title.includes(SITE_NAME) ? title : `${title} · ${SITE_NAME}`;
    document.title = fullTitle;

    if (description) upsertMeta('name', 'description', description);
    upsertMeta('property', 'og:title', fullTitle);
    upsertMeta('property', 'og:type', type);
    upsertMeta('property', 'og:site_name', SITE_NAME);
    if (description) upsertMeta('property', 'og:description', description);
    if (image) upsertMeta('property', 'og:image', image);
    upsertMeta('name', 'twitter:card', image ? 'summary_large_image' : 'summary');

    const url = `${window.location.origin}${canonicalPath ?? window.location.pathname}`;
    upsertCanonical(url);
    upsertMeta('property', 'og:url', url);

    let script: HTMLScriptElement | null = null;
    if (jsonLd) {
      script = document.createElement('script');
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify(jsonLd);
      document.head.appendChild(script);
    }

    return () => {
      if (script) document.head.removeChild(script);
    };
  }, [title, description, canonicalPath, image, jsonLd, type]);
}
